import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, lastValueFrom, Observable} from 'rxjs';

import {base} from '../api-config';
import {LIMIT, SKIP} from '../constants';

import {StaffMember, StaffMemberFilter, StaffMemberRequest, StaffMemberSearchResult} from '../models/staff-member';
import {
  Corrects,
  CorrectsSearchResult,
  ExamInvolvement,
  ExamInvolvementSearchResult,
  Supervises,
  SupervisesSearchResult,
  UnitExamInvolvement
} from '../models/exam-involvement';
import {HasDefaultParameters, Survey, SurveyFilter, SurveyRequest, SurveySearchResult} from '../models/survey';
import {Exam, ExamFilter, ExamRequest, ExamSearchResult} from '../models/exam';
import {ParameterSetRequest, ParameterSetResponse} from '../models/parameter';
import {SolutionQuality, UserSummary} from '../models/summary-data';
import {Upload} from '../models/upload';

const endpoint = `${base}/surveys`;

@Injectable({
  providedIn: 'root'
})
export class SurveyService {

  private selectedSurveySource = new BehaviorSubject<Survey | null>(null);

  selectedSurvey$ = this.selectedSurveySource.asObservable();

  constructor(private http: HttpClient) {
  }

  setSurvey(survey: Survey | null): void {
    this.selectedSurveySource.next(survey);
  }

  getSelectedSurveyId(): number {
    const survey = this.selectedSurveySource.value;
    return survey ? survey.id : -1;
  }

  // return an Observable for use as Angular resource
  getAllSurveys(skip: number = SKIP, limit: number = LIMIT, filter: SurveyFilter | null = null): Observable<SurveySearchResult> {

    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (filter) for (const [param, value] of Object.entries(filter)) if (value !== null) params = params.set(param, value);

    return this.http.get<SurveySearchResult>(`${endpoint}/`, {params});
  }

  // return a Promise
  getAll(skip: number = SKIP, limit: number = LIMIT, filter: SurveyFilter | null = null): Promise<SurveySearchResult> {
    return lastValueFrom(this.getAllSurveys(skip, limit, filter));
  }

  // return a Promise
  get(id: number): Promise<Survey> {
    return lastValueFrom(this.getSurvey(id));
  }

  // return an Observable for use as Angular resource
  getSurvey(id: number): Observable<Survey> {
    return this.http.get<Survey>(`${endpoint}/${id}`);
  }

  create(survey: SurveyRequest): Promise<Survey> {
    console.log("create survey in frontend:", survey)
    const obs = this.http.post<Survey>(`${endpoint}/`, survey);
    return lastValueFrom(obs);
  }

  update(id: number, survey: SurveyRequest): Promise<Survey> {
    const route = `${endpoint}/${id}`;
    const obs = this.http.put<Survey>(route, survey);
    return lastValueFrom(obs);
  }

  delete(id: number): Observable<string> {
    return this.http.delete<string>(`${endpoint}/${id}`);
  }

  publish(id: number): Promise<Survey> {
    const obs = this.http.put<Survey>(`${endpoint}/${id}/publish`, {});
    return lastValueFrom(obs);
  }

  upload(id: number, file: File): Promise<Upload> {
    const formData = new FormData()
    formData.append('upload', file, file.name);
    const route = `${endpoint}/${id}/upload`;
    const obs = this.http.post<Upload>(route, formData);
    return lastValueFrom(obs);
  }

  getExams(id: number, skip = SKIP, limit = LIMIT, filter: ExamFilter | null = null): Observable<ExamSearchResult> {

    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    //console.log(`filtering: ${JSON.stringify(filter)}`);
    if (filter) for (const [param, value] of Object.entries(filter)) if (value !== null) params = params.set(param, value);

    const route = `${endpoint}/${id}/exams`;
    return this.http.get<ExamSearchResult>(route, {params});
  }

  createExam(id: number, exam: ExamRequest): Observable<Exam> {
    const route = `${endpoint}/${id}/exams`;
    return this.http.post<Exam>(route, exam);
  }

  getStaffMembers(id: number, skip = SKIP, limit = LIMIT, filter: StaffMemberFilter | null = null): Observable<StaffMemberSearchResult> {

    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (filter) for (const [param, value] of Object.entries(filter)) if (value !== null) params = params.set(param, value);

    const route = `${endpoint}/${id}/staff_members`;
    return this.http.get<StaffMemberSearchResult>(route, {params});
  }

  createStaffMember(id: number, exam: StaffMemberRequest): Observable<StaffMember> {
    const route = `${endpoint}/${id}/staff_members`;
    return this.http.post<StaffMember>(route, exam);
  }

  getCorrections(id: number, staffMemberId: number | null = null, skip: number = SKIP, limit: number = LIMIT): Observable<ExamInvolvementSearchResult> {

    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (!!staffMemberId) params = params.set('staff_member_id', staffMemberId);

    const route = `${endpoint}/${id}/staff_members/corrections`;
    return this.http.get<ExamInvolvementSearchResult>(route, {params});
  }

  getCorrectionsSuggested(id: number, staffMemberId: number | null = null, skip: number = SKIP, limit: number = LIMIT): Observable<ExamInvolvementSearchResult> {

    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (!!staffMemberId) params = params.set('staff_member_id', staffMemberId);

    const route = `${endpoint}/${id}/staff_members/corrections/suggested`;
    return this.http.get<ExamInvolvementSearchResult>(route, {params});
  }

  getCorrectionsExempt(id: number, staffMemberId: number | null = null, skip: number = SKIP, limit: number = LIMIT): Observable<ExamInvolvementSearchResult> {

    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (!!staffMemberId) params = params.set('staff_member_id', staffMemberId);

    const route = `${endpoint}/${id}/staff_members/corrections/exempt`;
    return this.http.get<ExamInvolvementSearchResult>(route, {params});
  }

  getSupervisions(id: number, staffMemberId: number | null = null, begin: Date | null = null, end: Date | null = null, skip: number = SKIP, limit: number = LIMIT,): Observable<ExamInvolvementSearchResult> {

    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (staffMemberId) params = params.set('staff_member_id', staffMemberId);

    if (begin) params = params.set('begin', begin.toString());

    if (end) params = params.set('end', end.toString());

    const route = `${endpoint}/${id}/staff_members/supervisions`;
    return this.http.get<ExamInvolvementSearchResult>(route, {params});
  }

  getSupervisionsSuggested(id: number, staffMemberId: number | null = null, begin: Date | null = null, end: Date | null = null, skip: number = SKIP, limit: number = LIMIT,): Observable<ExamInvolvementSearchResult> {

    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (staffMemberId) params = params.set('staff_member_id', staffMemberId);

    if (begin) params = params.set('begin', begin.toString());

    if (end) params = params.set('end', end.toString());

    const route = `${endpoint}/${id}/staff_members/supervisions/suggested`;
    return this.http.get<ExamInvolvementSearchResult>(route, {params});
  }

  getSupervisionsExempt(id: number, staffMemberId: number | null = null, begin: Date | null = null, end: Date | null = null, skip: number = SKIP, limit: number = LIMIT,): Observable<ExamInvolvementSearchResult> {

    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (staffMemberId) params = params.set('staff_member_id', staffMemberId);

    if (begin) params = params.set('begin', begin.toString());

    if (end) params = params.set('end', end.toString());

    const route = `${endpoint}/${id}/staff_members/supervisions/exempt`;
    return this.http.get<ExamInvolvementSearchResult>(route, {params});
  }

  getDefaultParameters(id: number): Observable<ParameterSetResponse | null> {
    const route = `${endpoint}/${id}/default_parameters`;
    return this.http.get<ParameterSetResponse | null>(route);
  }

  setDefaultParameters(id: number, parametersId: number): Observable<HasDefaultParameters> {
    const route = `${endpoint}/${id}/default_parameters/${parametersId}`;
    return this.http.post<HasDefaultParameters>(route, {});
  }

  getCorrecting(id: number, examId: number, skip: number = SKIP, limit: number = LIMIT): Promise<CorrectsSearchResult> {

    const params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    const route = `${endpoint}/${id}/exams/${examId}/correction`;
    return lastValueFrom(this.http.get<CorrectsSearchResult>(route, {params}));
  }

  addCorrecting(id: number, examId: number, staffMemberId: number, time: number): Promise<Corrects> {

    const params = new HttpParams()
      .set('time', time);

    const route = `${endpoint}/${id}/exams/${examId}/correction/${staffMemberId}`;
    return lastValueFrom(this.http.post<Corrects>(route, {}, {params}));
  }

  removeCorrecting(id: number, examId: number, staffMemberId: number): Promise<string> {
    const route = `${endpoint}/${id}/exams/${examId}/correction/${staffMemberId}`;
    return lastValueFrom(this.http.delete<string>(route, {}));
  }

  getSupervising(id: number, examId: number, skip: number = SKIP, limit: number = LIMIT): Promise<SupervisesSearchResult> {

    const params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    const route = `${endpoint}/${id}/exams/${examId}/supervision`;
    return lastValueFrom(this.http.get<SupervisesSearchResult>(route, {params}));
  }

  addSupervising(id: number, examId: number, staffMemberId: number, roomId: number | null): Promise<Supervises> {

    let params = new HttpParams()

    if (roomId) params = params.set('room_id', roomId);

    const route = `${endpoint}/${id}/exams/${examId}/supervision/${staffMemberId}`;
    return lastValueFrom(this.http.post<Supervises>(route, {}, {params}));
  }

  removeSupervising(id: number, examId: number, staffMemberId: number): Promise<string> {
    const route = `${endpoint}/${id}/exams/${examId}/supervision/${staffMemberId}`;
    return lastValueFrom(this.http.delete<string>(route, {}));
  }

  getSuggestedCorrecting(id: number, examId: number, skip: number = SKIP, limit: number = LIMIT): Promise<CorrectsSearchResult> {

    const params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    const route = `${endpoint}/${id}/exams/${examId}/correction/suggested`;
    return lastValueFrom(this.http.get<CorrectsSearchResult>(route, {params}));
  }

  getSuggestedSupervising(id: number, examId: number, skip: number = SKIP, limit: number = LIMIT): Promise<SupervisesSearchResult> {

    const params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    const route = `${endpoint}/${id}/exams/${examId}/supervision/suggested`;
    return lastValueFrom(this.http.get<SupervisesSearchResult>(route, {params}));
  }

  getExemptFromCorrecting(id: number, examId: number, skip: number = SKIP, limit: number = LIMIT): Promise<ExamInvolvementSearchResult> {

    const params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    const route = `${endpoint}/${id}/exams/${examId}/correction/exempt`;
    return lastValueFrom(this.http.get<ExamInvolvementSearchResult>(route, {params}));
  }

  exemptFromCorrecting(id: number, examId: number, staffMemberId: number): Promise<ExamInvolvement> {
    const route = `${endpoint}/${id}/exams/${examId}/correction/exempt/${staffMemberId}`;
    return lastValueFrom(this.http.post<ExamInvolvement>(route, {}));
  }

  undoExemptFromCorrecting(id: number, examId: number, staffMemberId: number): Promise<string> {
    const route = `${endpoint}/${id}/exams/${examId}/correction/exempt/${staffMemberId}`;
    return lastValueFrom(this.http.delete<string>(route, {}));
  }

  getExemptFromSupervising(id: number, examId: number, skip: number = SKIP, limit: number = LIMIT): Promise<ExamInvolvementSearchResult> {

    const params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    const route = `${endpoint}/${id}/exams/${examId}/supervision/exempt`;
    return lastValueFrom(this.http.get<ExamInvolvementSearchResult>(route, {params}));
  }

  exemptFromSupervising(id: number, examId: number, staffMemberId: number): Promise<ExamInvolvement> {
    const route = `${endpoint}/${id}/exams/${examId}/supervision/exempt/${staffMemberId}`;
    return lastValueFrom(this.http.post<ExamInvolvement>(route, {}));
  }

  undoExemptFromSupervising(id: number, examId: number, staffMemberId: number): Promise<string> {
    const route = `${endpoint}/${id}/exams/${examId}/supervision/exempt/${staffMemberId}`;
    return lastValueFrom(this.http.delete<string>(route));
  }

  getAllExemptFromCorrecting(id: number, examId: number): Observable<UnitExamInvolvement[]> {
    return this.http.get<UnitExamInvolvement[]>(`${endpoint}/${id}/exams/${examId}/correction/exempt_units`);
  }

  exemptAllFromCorrecting(id: number, examId: number, unitId: number): Observable<UnitExamInvolvement> {
    return this.http.post<UnitExamInvolvement>(`${endpoint}/${id}/exams/${examId}/correction/exempt_units/${unitId}`, {});
  }

  undoExemptAllFromCorrecting(id: number, examId: number, unitId: number): Observable<string> {
    return this.http.delete<string>(`${endpoint}/${id}/exams/${examId}/correction/exempt_units/${unitId}`);
  }

  getAllExemptFromSupervising(id: number, examId: number): Observable<UnitExamInvolvement[]> {
    const route = `${endpoint}/${id}/exams/${examId}/supervision/exempt_units`;
    return this.http.get<UnitExamInvolvement[]>(route);
  }

  exemptAllFromSupervising(id: number, examId: number, unitId: number): Observable<UnitExamInvolvement> {
    const route = `${endpoint}/${id}/exams/${examId}/supervision/exempt_units/${unitId}`;
    return this.http.post<UnitExamInvolvement>(route, {});
  }

  undoExemptAllFromSupervising(id: number, examId: number, unitId: number): Observable<string> {
    const route = `${endpoint}/${id}/exams/${examId}/supervision/exempt_units/${unitId}`;
    return this.http.delete<string>(route);
  }

  getAllEligibleForCorrecting(id: number, examId: number): Observable<UnitExamInvolvement[]> {
    const route = `${endpoint}/${id}/exams/${examId}/correction/eligible_units`;
    return this.http.get<UnitExamInvolvement[]>(route);
  }

  allowAllForCorrecting(id: number, examId: number, unitId: number): Observable<UnitExamInvolvement> {
    const route = `${endpoint}/${id}/exams/${examId}/correction/eligible_units/${unitId}`;
    return this.http.post<UnitExamInvolvement>(route, {});
  }

  undoAllowAllForCorrecting(id: number, examId: number, unitId: number): Observable<string> {
    const route = `${endpoint}/${id}/exams/${examId}/correction/eligible_units/${unitId}`;
    return this.http.delete<string>(route);
  }

  getAllEligibleForSupervising(id: number, examId: number): Observable<UnitExamInvolvement[]> {
    const route = `${endpoint}/${id}/exams/${examId}/supervision/eligible_units`;
    return this.http.get<UnitExamInvolvement[]>(route);
  }

  allowAllForSupervising(id: number, examId: number, unitId: number): Observable<UnitExamInvolvement> {
    const route = `${endpoint}/${id}/exams/${examId}/supervision/eligible_units/${unitId}`;
    return this.http.post<UnitExamInvolvement>(route, {});
  }

  undoAllowAllForSupervising(id: number, examId: number, unitId: number): Observable<string> {
    const route = `${endpoint}/${id}/exams/${examId}/supervision/eligible_units/${unitId}`;
    return this.http.delete<string>(route);
  }

  solve(id: number, parameters: ParameterSetRequest): Promise<string> {
    const route = `${endpoint}/${id}/solve`;
    return lastValueFrom(this.http.post<string>(route, parameters));
  }

  abort(id: number): Promise<string> {
    const route = `${endpoint}/${id}/solve`;
    return lastValueFrom(this.http.delete<string>(route));
  }

  getUserSummary(id: number): Promise<UserSummary> {
    const route = `${endpoint}/${id}/user_summary`;
    return lastValueFrom(this.http.get<UserSummary>(route));
  }

  getSolutionSummary(id: number): Promise<SolutionQuality> {
    const route = `${endpoint}/${id}/solution_summary`;
    return lastValueFrom(this.http.get<SolutionQuality>(route));
  }

  getPreferencesExport(id: number): Observable<Blob> {
    const route = `${endpoint}/${id}/export_preferences`;
    return this.http.get(route, {responseType: 'blob'});
  }

  getAllocExport_2_0(id: number): Observable<string> {
    const route = `${endpoint}/${id}/export_alloc_protocol_2_0`;
    return this.http.get(route, {responseType: 'text'});
  }

  getCsv(id: number): Observable<string> {
    const route = `${endpoint}/${id}/export_to_csv`;
    return this.http.get(route, {responseType: 'text'});
  }
}
